<!--
  ApiTokens -- SuperAdmin API Tokens page.

  _Follow-up to PR #3810, which shipped this as a CARD on Platform Settings._

  Jake, 2026-09-26, having used it on production: give it its own page called
  "API Tokens" linked under Settings, replace the free-text expiry with a real
  date/time picker showing local time and sending UTC, and EXPLAIN WHAT THE
  SCOPE CHECKBOXES MEAN -- verbatim: "Why are there sanmar_ingest and
  deconetwork checkboxes? Are those scopes? What do they mean? (There's no
  explanation in the UI.)"

  "LINKED UNDER SETTINGS" MEANT THE LEFT NAV, not a link on the Settings page.
  #3811 read it the second way and shipped a card at the bottom of Platform
  Settings; Jake clarified: "a link directly under the Settings link in the left
  nav, labeled 'API Tokens'". The entry is sa-nav-api-tokens in
  SuperAdminLayout.vue, between Settings and Operations, and its POSITION is
  asserted (layouts/__tests__/superadmin-layout-api-tokens-nav.spec.js) because
  the position was the part that got lost. The Settings-page card is gone, and
  so is this page's "back to Settings" link -- see the header template.

  Scoped, revocable service-to-service credentials for partners
  (tf-deconetwork) and our own SanMar chip runner. Five endpoints, all gated
  on `superadmin.settings`:

    GET  /api/internal-api-tokens              the inventory, prefix only
    GET  /api/internal-api-tokens/scopes       the closed scope vocabulary
    POST /api/internal-api-tokens/mint         returns the cleartext ONCE
    POST /api/internal-api-tokens/<id>/reveal  returns it again, audited
    POST /api/internal-api-tokens/<id>/revoke  marks revoked, never deletes

  WHY A PAGE AND NOT A CARD
  =========================
  Not only because Jake asked. The card was one of a dozen on a settings page
  whose mount fires a dozen GETs, so an unrelated 403 on a decoration-methods
  key would take the credential surface down with it -- and the inverse, a
  token-list failure painting an error in the middle of a settings page, reads
  as "settings is broken". A credential surface with its own route fails
  independently, and this page fetches NOTHING but its own two endpoints (a
  test asserts no /platform-settings/ call is made).

  THE SCOPE EXPLANATION IS THE POINT OF THIS CHANGE
  ================================================
  The isolation these scopes provide is the entire reason the system replaced
  a single shared THREADFLOW_INGEST_TOKEN: before it, issuing a partner a
  credential meant giving them the chip runner's access too. That reasoning
  lived only in models/internal_api_token.py, so on screen an operator saw two
  slugs and two checkboxes -- and the cheapest action is to tick both, which
  re-creates precisely the posture the change removed.

  So each scope carries its OWN help paragraph naming whose it is, and a
  shared note states the isolation and warns that both ticked is wider than
  either caller needs. It is rendered text rather than a `title` tooltip: a
  tooltip requires knowing there is something to hover, is invisible on touch,
  and hides exactly the stakes an operator needs before clicking.

  The copy is bound by tests (see __tests__/api-tokens-page.spec.js), so
  deleting it fails a test rather than passing quietly -- the same treatment
  #3810 gave the password-manager sentence.

  THE SCOPE DESCRIPTIONS ARE KEYED, NOT POSITIONAL
  ===============================================
  `SCOPE_COPY` is a map from scope slug to copy, and the vocabulary itself is
  still FETCHED from /scopes. A slug the server names but this page has no
  copy for still renders a checkbox, with a paragraph that says the
  description is not documented here -- not an invented one, which an operator
  would act on, and not a bare checkbox, which is the defect this whole change
  fixes.

  NO PASSWORD RE-PROMPT ON REVEAL. Jake, 2026-09-25: "Stripe doesn't re-prompt
  for passwords. I think we can skip it." The gate is superadmin.settings plus
  the audit row the server writes. Do not add the friction back believing it
  was an oversight.

  THE LIST SHOWS A PREFIX, NEVER A TOKEN. The cleartext reaches this page in
  exactly two responses: the mint (once) and an explicit per-row reveal, which
  writes an audit row server-side. A reveal is a SEPARATE request on click, so
  rendering the inventory cannot display a credential even if the list
  endpoint were later widened to return one -- and a test feeds the component
  a list row carrying a cleartext to prove it.
-->
<template>
    <section class="sa-tokens" data-testid="sa-api-tokens-page">
        <header class="sa-tokens__header">
            <!--
              NO BACK LINK, deliberately. One stood here with the reason "this
              page is reached from a card on /admin/settings and has no SA
              sidebar entry of its own, so without it the only route back is
              the browser button". That reason expired: the page now has its own
              sidebar entry (sa-nav-api-tokens, directly under Settings), so the
              nav is the route back from here and from anywhere else.

              Keeping it would also misdescribe the IA -- "back to Settings" on
              a page that is Settings' SIBLING in the nav implies this lives
              under Settings. The convention here agrees: the only other back
              link on an SA page is UserDetail.vue's, and that is a detail page
              nested under a list.

              Asserted absent in __tests__/api-tokens-page.spec.js so it cannot
              drift back in.
            -->
            <h1 class="sa-tokens__title">API Tokens</h1>
            <p class="sa-tokens__lede">
                Scoped credentials for service-to-service callers -- the
                ThreadFlow Decorator back office and our own SanMar chip
                runner. Each token names its holder, is limited to the
                endpoints its scopes cover, and can be revoked without
                affecting anyone else.
            </p>
            <p
                class="sa-tokens__warn"
                data-testid="sa-setting-internal-api-tokens-handling-note"
            >
                Hand a token over through a password manager or another secrets
                channel. Do not email it -- the natural next step after minting
                is to send it to the partner, and a mail thread gets forwarded.
            </p>
        </header>

        <div class="sa-tokens__card" data-testid="sa-setting-internal-api-tokens">
            <h2 class="sa-tokens__section-title">Existing tokens</h2>

            <!-- The inventory. -->
            <div
                class="sa-tokens__list"
                data-testid="sa-setting-internal-api-tokens-list"
            >
                <!--
                  THE EMPTY STATE REQUIRES A SUCCESSFUL FETCH, not merely an
                  empty array. `tokens` is also empty after a FAILED fetch, and
                  "No tokens have been minted" is a claim about the database
                  that we cannot make when we could not read it. A reader who
                  takes it at face value concludes the partner has no
                  credential and mints a duplicate. `tokensLoaded` separates
                  the two; the error paragraph paints instead.
                -->
                <p
                    v-if="tokensLoaded && !tokens.length"
                    class="sa-tokens__help"
                    data-testid="sa-setting-internal-api-tokens-empty"
                >
                    No tokens have been minted. Every caller currently
                    authenticates with the legacy shared environment variable.
                </p>

                <!--
                  Plain v-for, NOT a v-else paired with the empty state above.
                  With `tokensLoaded` in that condition, `v-else` would read as
                  "the fetch failed OR there are rows" -- and on a failure it
                  would iterate an empty array, so nothing would paint and the
                  mistake would be invisible rather than harmless.
                -->
                <div
                    v-for="t in tokens"
                    :key="t.id"
                    class="sa-tokens__row"
                    :data-testid="`sa-setting-internal-api-tokens-row-${t.id}`"
                >
                    <div class="sa-tokens__meta">
                        <span class="sa-tokens__name">{{ t.name }}</span>
                        <code class="sa-tokens__prefix">
                            {{ t.token_prefix }}&hellip;
                        </code>
                        <span class="sa-tokens__scopes">
                            {{ (t.scopes || []).join(", ") }}
                        </span>
                        <!--
                          "never" rather than a blank cell. This is the question
                          the column exists to answer, and a blank reads as
                          "unknown" and prompts no action -- while "never" tells
                          an operator the partner has not wired the token up
                          yet.
                        -->
                        <span class="sa-tokens__used">
                            Last used:
                            {{ t.last_used_at ? t.last_used_at : "never" }}
                        </span>
                        <span v-if="t.revoked_at" class="sa-tokens__revoked">
                            Revoked {{ t.revoked_at }}
                        </span>
                        <!--
                          LABELLED UTC. The list is where an operator later
                          CHECKS an expiry, so an unlabelled timestamp here
                          re-introduces exactly the ambiguity the picker below
                          removes at mint time. `expires_at` comes back naive
                          from a TIMESTAMP WITHOUT TIME ZONE column and IS UTC,
                          so the label is a statement of fact rather than a
                          conversion.
                        -->
                        <span v-if="t.expires_at" class="sa-tokens__expires">
                            Expires {{ formatStoredUtc(t.expires_at) }}
                        </span>
                    </div>

                    <div class="sa-tokens__actions">
                        <!--
                          Reveal stays available on a REVOKED token,
                          deliberately, mirroring the service: an operator
                          investigating a leak needs the string in order to
                          grep for it.
                        -->
                        <button
                            type="button"
                            class="sa-tokens__button"
                            :disabled="tokensBusy"
                            :data-testid="`sa-setting-internal-api-tokens-reveal-${t.id}`"
                            @click="onRevealToken(t.id)"
                        >
                            Reveal
                        </button>
                        <button
                            v-if="!t.revoked_at"
                            type="button"
                            class="sa-tokens__button sa-tokens__button--danger"
                            :disabled="tokensBusy"
                            :data-testid="`sa-setting-internal-api-tokens-revoke-${t.id}`"
                            @click="onAskRevokeToken(t.id)"
                        >
                            Revoke
                        </button>
                        <!--
                          DELETE IS OFFERED ONLY ON A REVOKED TOKEN, and the
                          `v-if` is the point rather than a convenience. A live
                          credential removed from the inventory STILL
                          AUTHENTICATES, and nothing on any screen would say so
                          -- a standing grant no operator can see. The service
                          refuses it too (400), but an operator who has to be
                          told no by a server was offered the wrong affordance
                          in the first place.

                          Note this is the exact complement of the Revoke
                          button above: a row shows Revoke or Delete, never
                          both, which also keeps the two destructive actions
                          from sitting side by side.
                        -->
                        <button
                            v-if="t.revoked_at"
                            type="button"
                            class="sa-tokens__button sa-tokens__button--danger"
                            :disabled="tokensBusy"
                            :data-testid="`sa-setting-internal-api-tokens-delete-${t.id}`"
                            @click="onAskDeleteToken(t.id)"
                        >
                            Delete
                        </button>
                    </div>

                    <p
                        v-if="revealedTokens[t.id]"
                        class="sa-tokens__revealed"
                        :data-testid="`sa-setting-internal-api-tokens-revealed-${t.id}`"
                    >
                        <code>{{ revealedTokens[t.id] }}</code>
                        <button
                            type="button"
                            class="sa-tokens__button"
                            :data-testid="`sa-setting-internal-api-tokens-revealed-copy-${t.id}`"
                            @click="onCopy(revealedTokens[t.id])"
                        >
                            Copy
                        </button>
                    </p>

                    <!--
                      The confirmation NAMES THE HOLDER. Revoking a partner's
                      credential breaks their integration until a new one is
                      issued, and an unnamed "are you sure?" is the shape that
                      gets clicked through.
                    -->
                    <div
                        v-if="revokeAsking === t.id"
                        class="sa-tokens__confirm"
                        :data-testid="`sa-setting-internal-api-tokens-revoke-confirm-${t.id}`"
                    >
                        <p class="sa-tokens__help">
                            Revoke the token held by
                            <strong>{{ t.name }}</strong>? Their integration
                            stops working immediately and stays broken until you
                            issue a replacement. The row is kept for the audit
                            trail.
                        </p>
                        <button
                            type="button"
                            class="sa-tokens__button sa-tokens__button--danger"
                            :disabled="tokensBusy"
                            :data-testid="`sa-setting-internal-api-tokens-revoke-yes-${t.id}`"
                            @click="onRevokeToken(t.id)"
                        >
                            Revoke it
                        </button>
                        <button
                            type="button"
                            class="sa-tokens__button"
                            :data-testid="`sa-setting-internal-api-tokens-revoke-no-${t.id}`"
                            @click="revokeAsking = null"
                        >
                            Keep it
                        </button>
                    </div>

                    <!--
                      Delete confirmation. NAMES THE HOLDER, same posture as
                      revoke: an unnamed "are you sure?" is the shape that gets
                      clicked through, and clearing the wrong row hides the
                      audit record an investigation would want.

                      THE COPY SAYS THE RECORD IS KEPT, and that sentence is
                      tested. "Delete" implies destruction; what actually
                      happens is that the row is hidden and every audit fact
                      survives. An operator who believes they are destroying
                      evidence either hesitates to tidy up or -- worse --
                      believes they HAVE destroyed it, which is wrong in the
                      more dangerous direction.
                    -->
                    <div
                        v-if="deleteAsking === t.id"
                        class="sa-tokens__confirm"
                        :data-testid="`sa-setting-internal-api-tokens-delete-confirm-${t.id}`"
                    >
                        <p class="sa-tokens__help">
                            Remove the revoked token held by
                            <strong>{{ t.name }}</strong> from this list? It
                            already authenticates nothing. The row and its
                            audit history are KEPT -- who issued it, when, and
                            when it was revoked -- so a later investigation can
                            still read them. This only clears it off the
                            console.
                        </p>
                        <button
                            type="button"
                            class="sa-tokens__button sa-tokens__button--danger"
                            :disabled="tokensBusy"
                            :data-testid="`sa-setting-internal-api-tokens-delete-yes-${t.id}`"
                            @click="onDeleteToken(t.id)"
                        >
                            Remove it
                        </button>
                        <button
                            type="button"
                            class="sa-tokens__button"
                            :data-testid="`sa-setting-internal-api-tokens-delete-no-${t.id}`"
                            @click="deleteAsking = null"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Mint. -->
        <div class="sa-tokens__card" data-testid="sa-setting-internal-api-tokens-mint">
            <h2 class="sa-tokens__section-title">Mint a new token</h2>

            <label class="sa-tokens__label" for="sa-tokens-mint-name">
                Who is this token for?
            </label>
            <input
                id="sa-tokens-mint-name"
                v-model="mintName"
                type="text"
                class="sa-tokens__input"
                placeholder="tf-deconetwork (production)"
                :disabled="tokensBusy"
                data-testid="sa-setting-internal-api-tokens-mint-name"
            />
            <p class="sa-tokens__help">
                Name the holder, not the purpose. This is what a later leak
                investigation reads.
            </p>

            <!--
              ============================================================
              SCOPES -- the part Jake asked for, and the reason this page
              exists as its own unit of work.
              ============================================================
              Each checkbox carries its own paragraph saying WHOSE caller it
              is, because "which of these do I tick for a partner?" is the
              question an operator actually has and the slug alone does not
              answer it. The isolation note below states what the scopes DO,
              which is what makes ticking both a decision rather than a
              formality.
            -->
            <fieldset class="sa-tokens__scopes-fieldset">
                <legend class="sa-tokens__label">
                    Scopes -- which endpoints may this token call?
                </legend>

                <p
                    v-if="!tokenScopes.length"
                    class="sa-tokens__warn"
                    data-testid="sa-setting-internal-api-tokens-scopes-unavailable"
                >
                    The scope list could not be loaded, so no token can be
                    minted right now. Reload the page; if it stays empty, the
                    API is not answering
                    <code>/api/internal-api-tokens/scopes</code>. No scope is
                    guessed here on purpose -- an invented one would be
                    rejected at mint time by a form that presented it as
                    valid.
                </p>

                <ul v-else class="sa-tokens__scope-list">
                    <li
                        v-for="s in tokenScopes"
                        :key="s"
                        class="sa-tokens__scope-row"
                    >
                        <label
                            class="sa-tokens__scope-label"
                            :for="`sa-setting-internal-api-tokens-scope-${s}`"
                        >
                            <input
                                :id="`sa-setting-internal-api-tokens-scope-${s}`"
                                type="checkbox"
                                class="sa-tokens__checkbox"
                                :checked="mintScopes.includes(s)"
                                :disabled="tokensBusy"
                                :data-testid="`sa-setting-internal-api-tokens-scope-${s}`"
                                @change="onMintScopeToggle(s)"
                            />
                            <span class="sa-tokens__scope-heading">
                                <code class="sa-tokens__scope-slug">{{ s }}</code>
                                <span class="sa-tokens__scope-holder">
                                    {{ scopeCopy(s).holder }}
                                </span>
                            </span>
                        </label>
                        <p
                            class="sa-tokens__scope-help"
                            :data-testid="`sa-setting-internal-api-tokens-scope-help-${s}`"
                        >
                            {{ scopeCopy(s).description }}
                        </p>
                    </li>
                </ul>

                <!--
                  THE ISOLATION, STATED. Verified in source rather than
                  asserted from memory: services/internal_api_tokens.py
                  verify_token returns None when `required_scope not in
                  scopes` (line 352-364, whose own comment reads "THE POINT OF
                  THE WHOLE CHANGE"), and models/auth_decorators.py
                  requires_internal_token then refuses the request.

                  The "both" sentence is the operative one. An operator with
                  no information ticks everything, and for a partner that
                  means handing over our own chip runner's access -- the
                  shared-token posture this system was built to end.
                -->
                <p
                    class="sa-tokens__warn"
                    data-testid="sa-setting-internal-api-tokens-scope-isolation-note"
                >
                    A token is refused on any endpoint outside its scopes, so
                    these boxes are the boundary and not a label. Tick only
                    what this holder calls: ticking both grants one credential
                    access to the partner's endpoints AND our internal chip
                    runner, which is the shared-credential arrangement scoped
                    tokens replaced. If you are unsure, mint two tokens.
                </p>
            </fieldset>

            <!--
              ============================================================
              EXPIRY -- a real date/time control, local in, UTC out.
              ============================================================
              `datetime-local` is the native control and shows LOCAL time by
              construction, which is half of what Jake asked for. The other
              half is the conversion, and it is NOT cosmetic:
              endpoints/internal_api_tokens.py:141-142 assumes a NAIVE string
              is UTC, so posting this input's own value would store the
              wall-clock digits as UTC -- seven hours early in US Pacific --
              with the operator's own number visible in the list and no way to
              tell it is wrong.

              So utils/tokenExpiry converts to an explicit `...Z` instant, and
              the UTC read-back below is displayed so the operator can SEE
              what will be stored. `min` is now, in local terms, so the
              control itself discourages the past date the server would
              reject.
            -->
            <label class="sa-tokens__label" for="sa-tokens-mint-expires">
                Expires (optional) -- your local time
            </label>
            <input
                id="sa-tokens-mint-expires"
                v-model="mintExpiresLocal"
                type="datetime-local"
                class="sa-tokens__input"
                :min="minExpiresLocal"
                :disabled="tokensBusy"
                data-testid="sa-setting-internal-api-tokens-mint-expires"
            />
            <p class="sa-tokens__help">
                Leave this blank for a token that never expires. Pick a time in
                your own zone; it is converted below and sent as UTC.
            </p>
            <p
                v-if="mintExpiresUtcDisplay"
                class="sa-tokens__readback"
                data-testid="sa-setting-internal-api-tokens-mint-expires-utc"
            >
                Stored as {{ mintExpiresUtcDisplay }}
            </p>

            <button
                type="button"
                class="sa-tokens__button sa-tokens__button--primary"
                :disabled="tokensBusy"
                data-testid="sa-setting-internal-api-tokens-mint-submit"
                @click="onMintToken"
            >
                Mint token
            </button>

            <!--
              The freshly minted token. The ONLY place its cleartext appears
              without an audited reveal, which is why the copy says to store it
              now: an operator who does not know that closes this panel and
              generates an avoidable audit entry -- or mints a second token
              believing the first is lost.
            -->
            <div
                v-if="mintedToken"
                class="sa-tokens__minted"
                data-testid="sa-setting-internal-api-tokens-minted"
            >
                <p class="sa-tokens__label">
                    Token for {{ mintedToken.name }}
                </p>
                <p
                    class="sa-tokens__revealed"
                    data-testid="sa-setting-internal-api-tokens-minted-value"
                >
                    <code>{{ mintedToken.token }}</code>
                </p>
                <button
                    type="button"
                    class="sa-tokens__button"
                    data-testid="sa-setting-internal-api-tokens-minted-copy"
                    @click="onCopy(mintedToken.token)"
                >
                    Copy
                </button>
                <p class="sa-tokens__warn">
                    Store this now, in a password manager. Viewing it again
                    later is possible but every reveal is recorded in the audit
                    log.
                </p>
            </div>

            <p
                v-if="tokensErrorMsg"
                class="sa-tokens__error"
                data-testid="sa-setting-internal-api-tokens-error"
            >
                {{ tokensErrorMsg }}
            </p>
        </div>
    </section>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import axios from "@/axios";

import {
    localToUtcInstant,
    formatUtcForDisplay,
    localInputFromDate,
    offsetForLocalValue,
} from "@/utils/tokenExpiry";

// NOT under /platform-settings: these are rows in their own table, not a
// key-value knob, and the verbs are create/read/reveal/revoke rather than a
// GET/PATCH pair.
const TOKENS_URL = "/api/internal-api-tokens";
// The scope vocabulary is FETCHED, not hardcoded. It is closed by a DB CHECK
// constraint, so a hardcoded copy in the SPA would drift the moment a third
// caller appears -- and the failure mode is an SA unable to mint the token the
// new caller needs, with the option simply absent from the form.
const TOKEN_SCOPES_URL = "/api/internal-api-tokens/scopes";

/**
 * What each scope MEANS, in the operator's language.
 *
 * THIS MAP IS THE ANSWER TO JAKE'S QUESTION. The facts come from source, not
 * from memory -- each `deconetwork` endpoint was located before this copy was
 * written:
 *
 *   deconetwork    api/src/api/internal/deconetwork_fulfillment.py:266
 *                  api/src/api/internal/deconetwork_payable_results.py:181
 *                  api/src/api/internal/distributor_supplier_credentials.py:98
 *   sanmar_ingest  api/src/api/internal/sanmar_color_chips.py:107
 *
 * KEYED BY SLUG, NOT POSITIONAL, and the vocabulary is still fetched. A slug
 * the server names but this page has no copy for falls through to
 * `UNDOCUMENTED_SCOPE` -- which says so. Inventing a description would be
 * worse than omitting one, because an operator would act on it.
 *
 * `holder` is the one-phrase answer to "is this ours or theirs?", shown beside
 * the checkbox. `description` is the paragraph beneath it.
 */
const SCOPE_COPY = {
    deconetwork: {
        holder: "the partner's",
        description:
            "Lets the ThreadFlow Decorator back office (Dustin's system) " +
            "call us: fulfillment events, payable results, and supplier " +
            "credential lookups. This is the scope you issue OUTSIDE the " +
            "company -- tick it for a partner integration and nothing else.",
    },
    sanmar_ingest: {
        holder: "ours (internal)",
        description:
            "Lets our own SanMar colour-chip runner post ingested chip data. " +
            "It is an internal job of ours, not a partner -- a token for the " +
            "chip pipeline needs this scope and no other.",
    },
};

/**
 * Copy for a scope the server named that this page does not know.
 *
 * Says it is undocumented HERE and points at the model, rather than leaving
 * the checkbox bare -- a bare checkbox is exactly the defect this change
 * fixes, and it would be strange to fix it for two scopes and reintroduce it
 * for the third.
 */
const UNDOCUMENTED_SCOPE = {
    holder: "unknown",
    description:
        "This scope is not documented on this page yet. Check " +
        "api/src/models/internal_api_token.py and the endpoint that requires " +
        "it before issuing a token with it -- do not guess at what it grants.",
};

export default {
    name: "ApiTokens",

    setup() {
        // `revealedTokens` is keyed by token id and holds ONLY values a human
        // explicitly asked for. It starts empty on every mount, so a page
        // reload un-reveals everything -- a revealed credential must not
        // persist on screen across navigations.
        const tokens = ref([]);
        // Whether the inventory fetch SUCCEEDED. Distinct from
        // `tokens.length === 0`, which is also true after a failure -- see the
        // empty-state comment in the template for why conflating them is a
        // reader-facing bug and not a cosmetic one.
        const tokensLoaded = ref(false);
        const tokenScopes = ref([]);
        const revealedTokens = ref({});
        const tokensErrorMsg = ref("");
        const tokensBusy = ref(false);
        const mintName = ref("");
        const mintScopes = ref([]);
        // The PICKER's value: a naive local string, `YYYY-MM-DDTHH:mm`. Never
        // sent as-is; see `expiresAtWire`.
        const mintExpiresLocal = ref("");
        const mintedToken = ref(null);
        const revokeAsking = ref(null);
        // Which row is asking to be DELETED. A separate ref from
        // `revokeAsking`, and both are single-valued: two open destructive
        // prompts on one row -- or on two rows naming different holders -- is
        // how the wrong credential gets cleared. Opening either closes the
        // other; tests pin both.
        const deleteAsking = ref(null);

        /** Copy for one scope slug, falling back to "not documented here". */
        function scopeCopy(slug) {
            return SCOPE_COPY[slug] || UNDOCUMENTED_SCOPE;
        }

        /**
         * The picked local time as an explicit UTC instant, or null.
         *
         * THE OFFSET COMES FROM THE PICKED DATE, NOT FROM TODAY. Using
         * `new Date().getTimezoneOffset()` here was a real one-hour bug, found
         * in self-review: an expiry on the other side of a DST boundary from
         * today -- the NORMAL case, since expiries are set months out -- would
         * convert with the wrong offset. Measured on US Pacific: picking
         * 2027-01-15T09:30 in July produced 16:30Z where the true instant is
         * 17:30Z, silently, on the surface built to stop exactly that.
         *
         * `offsetForLocalValue` asks what offset applies AT the picked instant.
         * Pinned by the DST cases in utils/__tests__/tokenExpiry.spec.js, which
         * SKIP with an explicit statement rather than passing vacuously in a
         * zone without DST.
         */
        const expiresAtWire = computed(() =>
            localToUtcInstant(
                mintExpiresLocal.value,
                offsetForLocalValue(mintExpiresLocal.value)
            )
        );

        /** The UTC instant to SHOW, so the operator can see what is stored. */
        const mintExpiresUtcDisplay = computed(() =>
            formatUtcForDisplay(expiresAtWire.value)
        );

        /**
         * `min` for the picker: now, in local terms.
         *
         * Computed once at setup rather than as a live computed. A ref that
         * re-evaluated `new Date()` on every render would make `min` creep
         * forward while the operator is typing, and a value that was legal a
         * second ago becoming illegal mid-edit is a confusing control. The
         * server refuses a past expiry regardless, which is the real gate.
         */
        const minExpiresLocal = localInputFromDate(
            new Date(),
            new Date().getTimezoneOffset()
        );

        /**
         * Render a stored `expires_at` for the list, labelled UTC.
         *
         * The column is TIMESTAMP WITHOUT TIME ZONE and the value IS UTC, so
         * the serialized string arrives naive. `formatUtcForDisplay` parses it
         * as the instant it is; appending `Z` first is what makes that
         * explicit rather than relying on the JS engine's naive-string
         * behaviour (which for a date-time form is LOCAL, and would shift it).
         */
        function formatStoredUtc(value) {
            const raw = String(value ?? "").trim();
            if (!raw) return "";
            const asInstant = /[Zz]|[+-]\d{2}:?\d{2}$/.test(raw)
                ? raw
                : `${raw}Z`;
            return formatUtcForDisplay(asInstant);
        }

        /**
         * Pull a server-authored sentence out of an axios error.
         *
         * Carried over from PlatformSettings.vue unchanged. A 503 names
         * MFA_ENCRYPTION_KEY, and the fix is an env var rather than the
         * operator's input -- paraphrasing it would strand them.
         */
        function extractServerReason(err) {
            const data = err?.response?.data;
            if (!data) return "";
            if (typeof data === "object") {
                return data.message || data.detail || "";
            }
            if (typeof data !== "string") return "";
            // The <p> Flask puts the abort() description in. Non-greedy so a
            // later paragraph cannot be swallowed into the match.
            const paragraph = data.match(/<p>([\s\S]*?)<\/p>/i);
            const raw = paragraph ? paragraph[1] : data;
            const text = raw
                .replace(/<[^>]*>/g, "")
                .replace(/&#39;/g, "'")
                .replace(/&quot;/g, '"')
                .replace(/&amp;/g, "&")
                .replace(/&lt;/g, "<")
                .replace(/&gt;/g, ">")
                .replace(/\s+/g, " ")
                .trim();
            // A bare "<!doctype html>" with no <p> would yield noise; only
            // return something that looks like a sentence.
            return text.length > 3 ? text : "";
        }

        /**
         * Fetch the inventory. NEVER carries a cleartext token.
         *
         * A failed fetch must NOT fall through to the empty state. An empty
         * list and a failed fetch look identical to a reader who sees only
         * "no tokens", and the wrong conclusion -- "the partner has no
         * credential" -- leads to minting a duplicate.
         */
        async function loadTokens() {
            try {
                const resp = await axios.get(TOKENS_URL);
                const rows = resp?.data?.tokens;
                tokens.value = Array.isArray(rows) ? rows : [];
                tokensLoaded.value = true;
                tokensErrorMsg.value = "";
            } catch (e) {
                tokens.value = [];
                // NOT loaded. The empty state must not paint on a failure.
                tokensLoaded.value = false;
                tokensErrorMsg.value =
                    e?.response?.status === 403
                        ? "Permission denied. Only Super Admins can manage API tokens."
                        : "Could not load the API token list.";
            }
        }

        async function loadTokenScopes() {
            try {
                const resp = await axios.get(TOKEN_SCOPES_URL);
                const list = resp?.data?.scopes;
                tokenScopes.value = Array.isArray(list) ? list : [];
            } catch (e) {
                // Leave the list EMPTY rather than substituting a guess. An
                // invented vocabulary would offer a scope the backend rejects,
                // and the mint would 400 on a value the form presented as
                // valid. The template says so where the checkboxes would be.
                tokenScopes.value = [];
            }
        }

        function onMintScopeToggle(scope) {
            const next = mintScopes.value.includes(scope)
                ? mintScopes.value.filter((s) => s !== scope)
                : [...mintScopes.value, scope];
            mintScopes.value = next;
        }

        /**
         * Mint a token and show its cleartext once.
         *
         * The client-side refusals mirror rules the backend enforces anyway.
         * They are here so an operator meets the explanation rather than a 400
         * for something the form could have told them -- and the past-expiry
         * one earns its place now that a PICKER makes a past date one
         * mis-click away rather than a deliberate typed string.
         */
        async function onMintToken() {
            const name = (mintName.value || "").trim();
            if (!name) {
                tokensErrorMsg.value =
                    "Name the holder of this token before minting it.";
                return;
            }
            if (!mintScopes.value.length) {
                tokensErrorMsg.value =
                    "Choose at least one scope -- a token with no scopes can call nothing.";
                return;
            }

            const expiresAt = expiresAtWire.value;
            if (mintExpiresLocal.value && !expiresAt) {
                tokensErrorMsg.value =
                    "That expiry could not be read. Pick a date and time again.";
                return;
            }
            if (expiresAt && new Date(expiresAt) <= new Date()) {
                tokensErrorMsg.value =
                    "That expiry is in the past -- the token would be born " +
                    "expired and would authenticate nothing.";
                return;
            }

            tokensBusy.value = true;
            tokensErrorMsg.value = "";
            // Clear any previous mint BEFORE the request: a failed mint must
            // never leave the last successful token on screen, where it would
            // read as the new one.
            mintedToken.value = null;
            try {
                const resp = await axios.post(`${TOKENS_URL}/mint`, {
                    name,
                    scopes: [...mintScopes.value],
                    // An EXPLICIT UTC instant, or null. Never the picker's
                    // naive local string -- see the expiry block in the
                    // template for what that would store.
                    expires_at: expiresAt,
                });
                mintedToken.value = resp?.data || null;
                mintName.value = "";
                mintScopes.value = [];
                mintExpiresLocal.value = "";
                await loadTokens();
            } catch (e) {
                const serverReason = extractServerReason(e);
                tokensErrorMsg.value =
                    serverReason || "Could not mint the token.";
            } finally {
                tokensBusy.value = false;
            }
        }

        /**
         * Reveal one token's cleartext. A SEPARATE request, on click.
         *
         * No password re-prompt (Jake, 2026-09-25). The gate is
         * superadmin.settings plus the audit row the server writes.
         */
        async function onRevealToken(tokenId) {
            tokensBusy.value = true;
            tokensErrorMsg.value = "";
            try {
                const resp = await axios.post(`${TOKENS_URL}/${tokenId}/reveal`);
                const value = resp?.data?.token;
                if (value) {
                    revealedTokens.value = {
                        ...revealedTokens.value,
                        [tokenId]: value,
                    };
                }
            } catch (e) {
                // Drop any previously revealed value for this row. Leaving a
                // stale one on screen after a failed reveal would present an
                // old credential as the current one.
                const next = { ...revealedTokens.value };
                delete next[tokenId];
                revealedTokens.value = next;
                const serverReason = extractServerReason(e);
                tokensErrorMsg.value =
                    serverReason || "Could not reveal that token.";
            } finally {
                tokensBusy.value = false;
            }
        }

        function onAskRevokeToken(tokenId) {
            tokensErrorMsg.value = "";
            // Symmetric with onAskDeleteToken: opening one destructive prompt
            // closes the other. A live row offers no Delete button today so
            // this cannot currently fire, but the asymmetry would be a trap
            // for the next edit that changes when either control is shown.
            deleteAsking.value = null;
            revokeAsking.value = tokenId;
        }

        async function onRevokeToken(tokenId) {
            tokensBusy.value = true;
            tokensErrorMsg.value = "";
            try {
                await axios.post(`${TOKENS_URL}/${tokenId}/revoke`);
                revokeAsking.value = null;
                await loadTokens();
            } catch (e) {
                const serverReason = extractServerReason(e);
                tokensErrorMsg.value =
                    serverReason || "Could not revoke that token.";
            } finally {
                tokensBusy.value = false;
            }
        }

        function onAskDeleteToken(tokenId) {
            tokensErrorMsg.value = "";
            // Close any open revoke prompt. They are different destructive
            // actions with different consequences, and both panels open at
            // once is an invitation to click the wrong button.
            revokeAsking.value = null;
            deleteAsking.value = tokenId;
        }

        /**
         * Soft-delete a revoked token: clear it off this list, keep the row.
         *
         * Jake, 2026-09-26: "I'd like to be able to delete revoked tokens.
         * (Soft-delete is fine if easier.)"
         *
         * ON FAILURE THE CONFIRMATION STAYS OPEN. Closing it would leave the
         * operator looking at a row that is still there with the explanation
         * detached from the thing they clicked -- the row did not change, so
         * nothing about the surface would connect the error to the action.
         * Contrast `onRevokeToken`, which closes on SUCCESS only, for the same
         * reason.
         *
         * The list is re-read afterwards because the row is filtered out
         * server-side; without that the operator clicks and nothing visibly
         * happens, which reads as a broken button.
         */
        async function onDeleteToken(tokenId) {
            tokensBusy.value = true;
            tokensErrorMsg.value = "";
            try {
                await axios.post(`${TOKENS_URL}/${tokenId}/delete`);
                deleteAsking.value = null;
                await loadTokens();
            } catch (e) {
                const serverReason = extractServerReason(e);
                tokensErrorMsg.value =
                    serverReason || "Could not remove that token.";
            } finally {
                tokensBusy.value = false;
            }
        }

        /**
         * Copy a value to the clipboard, tolerating its absence.
         *
         * `navigator.clipboard` is undefined on a non-secure origin (every
         * http:// sandbox) and in jsdom. Failing silently is right here: the
         * value is already on screen and selectable, so a broken copy button
         * is an inconvenience, while an unhandled rejection would surface as a
         * console error and fail the ui-runner scenario's console gate.
         */
        async function onCopy(value) {
            if (!value) return;
            try {
                await navigator?.clipboard?.writeText?.(value);
            } catch (e) {
                // Deliberately swallowed -- see the docstring.
            }
        }

        onMounted(async () => {
            // Both, in parallel, and neither failure blanks the page: they are
            // separate concerns (what exists vs what can be minted) and the
            // template has a distinct empty/error state for each.
            await Promise.all([loadTokens(), loadTokenScopes()]);
        });

        return {
            tokens,
            tokensLoaded,
            tokenScopes,
            revealedTokens,
            tokensErrorMsg,
            tokensBusy,
            mintName,
            mintScopes,
            mintExpiresLocal,
            mintExpiresUtcDisplay,
            minExpiresLocal,
            mintedToken,
            revokeAsking,
            deleteAsking,
            scopeCopy,
            formatStoredUtc,
            onMintScopeToggle,
            onMintToken,
            onRevealToken,
            onAskRevokeToken,
            onRevokeToken,
            onAskDeleteToken,
            onDeleteToken,
            onCopy,
        };
    },
};
</script>

<style scoped>
/*
 * Visual language carried over from PlatformSettings.vue's cards, so the page
 * this card came off and the page it moved to look like the same product. The
 * class PREFIX changes (sa-settings__ -> sa-tokens__) because these are now
 * this page's own styles rather than a slice of a settings stylesheet.
 */
.sa-tokens {
    max-width: 720px;
}
.sa-tokens__header {
    margin-bottom: 24px;
}
.sa-tokens__title {
    font-size: 22px;
    font-weight: 600;
    color: #1a202c;
    margin: 0 0 6px;
}
.sa-tokens__lede {
    margin: 0;
    color: #4b5563;
    line-height: 1.5;
    font-size: 14px;
    max-width: 560px;
}
.sa-tokens__card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 16px 20px;
    margin-bottom: 24px;
}
.sa-tokens__section-title {
    font-size: 15px;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 12px;
}
.sa-tokens__help {
    margin: 4px 0 0;
    color: #4b5563;
    font-size: 13px;
    line-height: 1.5;
    max-width: 460px;
}
.sa-tokens__warn {
    margin: 14px 0 0;
    padding: 8px 12px;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 6px;
    color: #92400e;
    font-size: 12px;
    line-height: 1.45;
}
.sa-tokens__error {
    margin: 12px 0 0;
    color: #b91c1c;
    font-size: 0.875rem;
}
.sa-tokens__label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #1f2937;
    margin-top: 14px;
}
.sa-tokens__input {
    width: 100%;
    padding: 7px 10px;
    margin-top: 4px;
    border: 1px solid #cbd2dc;
    border-radius: 6px;
    font-size: 14px;
    color: #1f2937;
    background: #fff;
}
.sa-tokens__input:focus-visible {
    outline: 2px solid #2f6df6;
    outline-offset: 1px;
    border-color: #2f6df6;
}
.sa-tokens__input:disabled {
    background: #f3f4f6;
    color: #6b7280;
}
/* The UTC read-back. Monospace and boxed so it reads as a VALUE the system is
   reporting rather than as more prose -- it is the answer to "what will
   actually be stored?". */
.sa-tokens__readback {
    margin: 8px 0 0;
    padding: 6px 10px;
    background: #f7fafc;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 13px;
    color: #1f2937;
}

/* Scopes. */
.sa-tokens__scopes-fieldset {
    margin-top: 18px;
    padding: 0;
    border: 0;
}
.sa-tokens__scope-list {
    list-style: none;
    margin: 10px 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
}
.sa-tokens__scope-row {
    display: block;
}
.sa-tokens__scope-label {
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 14px;
    color: #1f2937;
    cursor: pointer;
}
.sa-tokens__checkbox {
    width: 16px;
    height: 16px;
    cursor: pointer;
}
.sa-tokens__scope-heading {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 8px;
}
.sa-tokens__scope-slug {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    background: #f7fafc;
    padding: 1px 5px;
    border-radius: 3px;
    font-size: 13px;
}
.sa-tokens__scope-holder {
    font-size: 12px;
    font-weight: 600;
    color: #4a5568;
}
/* Indented to the checkbox's text column so the paragraph reads as belonging
   to that scope and not to the group. */
.sa-tokens__scope-help {
    margin: 4px 0 0 24px;
    color: #4b5563;
    font-size: 12.5px;
    line-height: 1.5;
    max-width: 480px;
}

/* The inventory. */
.sa-tokens__list {
    margin: 12px 0 0;
}
.sa-tokens__row {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 8px;
}
.sa-tokens__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: baseline;
    font-size: 13px;
    color: #4a5568;
}
.sa-tokens__name {
    font-weight: 600;
    color: #1a202c;
}
.sa-tokens__prefix {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    background: #f7fafc;
    padding: 1px 5px;
    border-radius: 3px;
}
.sa-tokens__revoked {
    color: #c53030;
    font-weight: 600;
}
.sa-tokens__actions {
    display: flex;
    gap: 8px;
    margin-top: 8px;
}
.sa-tokens__button {
    font-size: 13px;
    padding: 4px 10px;
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
}
.sa-tokens__button:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}
.sa-tokens__button:focus-visible {
    outline: 2px solid #2f6df6;
    outline-offset: 2px;
}
.sa-tokens__button--danger {
    border-color: #fc8181;
    color: #c53030;
}
.sa-tokens__button--primary {
    margin-top: 16px;
    border-color: #2f6df6;
    color: #2f6df6;
    font-weight: 600;
}
/* A revealed or freshly minted credential. `word-break` matters: a 64-char
   token must WRAP rather than overflow, because a value clipped at the card
   edge gets copied incomplete by hand and then fails authentication with no
   visible cause. */
.sa-tokens__revealed {
    margin: 8px 0 0;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 13px;
    background: #f7fafc;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    padding: 8px;
    word-break: break-all;
}
.sa-tokens__confirm {
    margin-top: 8px;
    padding: 8px 10px;
    border: 1px solid #fc8181;
    border-radius: 4px;
    background: #fff5f5;
}
.sa-tokens__minted {
    margin-top: 12px;
    padding: 10px 12px;
    border: 1px solid #68d391;
    border-radius: 6px;
    background: #f0fff4;
}
</style>
